"use client";
// packages
import React, { useState, useEffect, useRef, useMemo, forwardRef } from "react";
import { Virtualizer, type VirtualizerHandle } from "virtua";
import kebabCase from "lodash/kebabCase";
import { useDebouncedState } from "@react-hookz/web";

// media
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";

// components
import { Button, Command, CommandList, CommandGroup, CommandInput, Popover, PopoverContent, PopoverTrigger, CommandEmpty } from "@/shared/components/ui";

// utils
import { cn } from "@/shared/utils";
import { searchBreeds, FuzzySearchableBreed } from "@/shared/utils/FuzzySearch";

// types
import { Option } from "@/shared/types/Quote.interface";
import { cva, VariantProps } from "class-variance-authority";

const iconVariants = cva("absolute right-4 opacity-50", {
    variants: {
        variant: {
            default: "size-4",
            "floating-inner": "size-6"
        }
    },
    defaultVariants: {
        variant: "default"
    }
});

interface ComboboxProps extends VariantProps<typeof iconVariants> {
    options: Option[];
    isLoading?: boolean;
    error?: string | null;
    placeholder?: string;
    searchPlaceholder?: string;
    value?: string;
    onChange?: (value: string) => void;
    keyByLabel?: boolean;
    height?: string;
    disabled?: boolean;
    triggerProps?: React.HTMLAttributes<HTMLButtonElement>;
    withContainer?: boolean;
    wrapperClass?: string;
    buttonVariant?: "outline" | "outline-floating";
    buttonSize?: "md" | "lg";
    enableFuzzySearch?: boolean;
    // Opt-in: when provided, the popover renders this instead of the search + options list
    // (e.g. CA breed field gated behind postal code). Receives a `close` callback to dismiss the popover.
    lockedContent?: (close: () => void) => React.ReactNode;
}

interface VirtualizedCommandProps {
    height: string;
    options: Option[];
    searchPlaceholder?: string;
    selectedOption: string;
    onSelectOption: (option: string) => void;
    keyByLabel?: boolean;
    enableFuzzySearch?: boolean;
}

// Convert Option to FuzzySearchableBreed format
function convertToFuzzySearchable(options: Option[]): FuzzySearchableBreed[] {
    const converted = options.map(option => ({
        value: option.value,
        label: option.label,
        otherNames: option.otherNames || []
    }));

    return converted;
}

// Virtualize dropdown options to handle large lists
const VirtualizedCommand = ({ height, options, searchPlaceholder, selectedOption, onSelectOption, keyByLabel, enableFuzzySearch = false }: VirtualizedCommandProps) => {
    const resultOptionHeight = 40;
    // Calculate the number of visible search results based on the height of the dropdown
    const visibleResults = useMemo(() => Math.floor(parseInt(height) / resultOptionHeight), [height]);
    // `searchValue` filters immediately on every keystroke; `debouncedSearchValue` (500ms) only drives the
    // scroll-to-top-after-typing behavior below.
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue, setDebouncedSearchValue] = useDebouncedState("", 500);
    // Derived, not stored: recomputes when `options` load/change asynchronously (e.g. CA breeds fetched
    // after a postal code is entered while the dropdown is already open), so a search typed during
    // loading still resolves instead of leaving the list stuck on "No match found".
    const filteredOptions = useMemo(() => {
        const search = searchValue.trim();
        if (!search) return options;
        if (enableFuzzySearch) return searchBreeds(search, convertToFuzzySearchable(options));
        return options.filter(option => option.label.toLowerCase().includes(search.toLowerCase()));
    }, [options, searchValue, enableFuzzySearch]);
    const parentRef = useRef<HTMLDivElement>(null);
    // Imperative handle of virtua's <Virtualizer>; the scroll container stays `CommandList` (parentRef).
    // (`@tanstack/react-virtual`'s `useVirtualizer` is hard-coded as incompatible by the React Compiler.)
    const virtualizerRef = useRef<VirtualizerHandle>(null);
    const commandInputPlaceholder = searchPlaceholder || "Search...";

    // If a User is searching for a breed
    // scroll to top of search results list when results are narrowed down, and User stops typing (debouncedSearchValue is set)
    useEffect(() => {
        // Consider results "narrowed" if the filtered list is short enough
        // (≤ 10x the number of visible results). This prevents auto-scrolling for very long lists
        // Ensures that when the user has typed enough to significantly reduce options, we scroll to the top.
        const narrowedSearchResults = filteredOptions.length <= visibleResults * 10;
        if (narrowedSearchResults && !!debouncedSearchValue) {
            virtualizerRef.current?.scrollToIndex(0, { align: "start" });
        }
    }, [filteredOptions, selectedOption, debouncedSearchValue, visibleResults]);

    // Centre the saved selection when the FULL list is showing (open, options loaded, search cleared).
    // While a search is active the rendered list is `filteredOptions`, whose indexes differ from
    // `options`, and scrolling belongs to the debounced effect above — so this one stands down rather
    // than centring on an index from the wrong list. (`filteredOptions` is deliberately not a dep: it
    // changes on every keystroke and would re-centre the list mid-typing.)
    useEffect(() => {
        if (searchValue.trim()) return;
        const selectedIndex = options.findIndex(option => option.value === selectedOption);
        if (selectedIndex === -1) return;
        virtualizerRef.current?.scrollToIndex(selectedIndex, { align: "center" });
    }, [options, selectedOption, searchValue]);

    const handleSearch = (search: string) => {
        setSearchValue(search);
        setDebouncedSearchValue(search);
    };

    // Highlight current option, and scroll, on arrow up/down key press
    const handleKeyDown = (event: React.KeyboardEvent) => {
        const container = parentRef.current;
        if (!container) return;

        const currentElement = container.querySelector("[aria-selected=true]");
        const currentIndex = currentElement ? filteredOptions.findIndex(option => option.value === currentElement?.getAttribute("data-value")) : -1;

        const updateSelection = (nextIndex: number) => {
            if (nextIndex < 0 || nextIndex >= filteredOptions.length) return;

            if (currentElement) {
                currentElement.removeAttribute("aria-selected");
            }
            const nextElement = container.querySelector(`[data-value="${filteredOptions[nextIndex]?.value}"]`);
            nextElement?.setAttribute("aria-selected", "true");
            virtualizerRef.current?.scrollToIndex(nextIndex, { align: "nearest" });
        };

        switch (event.key) {
            case "ArrowDown":
                updateSelection(currentIndex + 1);
                event.preventDefault();
                break;
            case "ArrowUp":
                updateSelection(currentIndex - 1);
                event.preventDefault();
                break;
            case "Enter":
                const newValue = currentElement?.getAttribute("data-value");
                if (newValue) {
                    onSelectOption(newValue);
                }
                break;
            default:
                break;
        }
    };

    return (
        <Command shouldFilter={false} onKeyDown={handleKeyDown}>
            <CommandInput onValueChange={handleSearch} placeholder={commandInputPlaceholder} />
            <CommandList
                ref={parentRef}
                style={{
                    height: height
                }}
                className="w-full overflow-auto"
                onWheel={e => e.stopPropagation()}
                onTouchMove={e => e.stopPropagation()}
            >
                <CommandEmpty>{filteredOptions.length === 0 && <div className="py-5 text-center text-content-secondary">No match found.</div>}</CommandEmpty>
                <CommandGroup>
                    {/* bufferSize 400px ≈ the previous overscan of 10 rows × 40px, so arrow-key navigation still finds the next row in the DOM */}
                    {/* `data` + function child: rows are created lazily for the viewport (+ buffer) only. A
                        `.map` over the whole catalog would build ~600–1000 elements on every keystroke. */}
                    <Virtualizer ref={virtualizerRef} scrollRef={parentRef} data={filteredOptions} itemSize={resultOptionHeight} bufferSize={resultOptionHeight * 10}>
                        {option => {
                            const selected = selectedOption === option.value;

                            return (
                                // key-event is handled in Command onKeyDown
                                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                                <div
                                    style={{ height: `${resultOptionHeight}px` }}
                                    tabIndex={-1} // focusable but not tabbable
                                    aria-label={option.label}
                                    className="flex w-full cursor-default select-none items-center rounded-sm p-2 text-sm text-content-primary hover:bg-background-secondary aria-selected:bg-background-secondary"
                                    key={keyByLabel ? option.label : option.value}
                                    onClick={() => onSelectOption(option.value)}
                                    aria-selected={selected || false}
                                    role="option"
                                    data-value={option.value}
                                >
                                    <Check size={16} className={cn("mr-2 size-4", selected ? "opacity-100" : "opacity-0")} />
                                    <span className="truncate" data-testid={kebabCase(option.label)}>
                                        {option.label}
                                    </span>
                                </div>
                            );
                        }}
                    </Virtualizer>
                </CommandGroup>
            </CommandList>
        </Command>
    );
};

export const Combobox = forwardRef<HTMLDivElement, ComboboxProps>(
    (
        {
            options,
            isLoading,
            error,
            placeholder = "",
            searchPlaceholder,
            value,
            onChange,
            keyByLabel,
            height = "250px",
            disabled,
            triggerProps,
            withContainer = false,
            wrapperClass,
            variant,
            buttonVariant = "outline",
            buttonSize = "md",
            enableFuzzySearch = false,
            lockedContent
        },
        ref
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const [open, setOpen] = useState(false);
        const [internalValue, setInternalValue] = useState(value || "");
        const [container, setContainer] = useState<HTMLElement | undefined>(undefined);

        useEffect(() => {
            if (withContainer && containerRef.current) {
                setContainer(containerRef.current);
            } else {
                setContainer(undefined);
            }
        }, [withContainer]);

        // `internalValue` is the uncontrolled fallback used when no `value` prop is supplied, so it has
        // to survive a re-render after `handleSelect`. When a `value` IS supplied it wins: re-sync on
        // every change of it, using React's "adjusting state when a prop changes" pattern.
        const [prevValue, setPrevValue] = useState(value);
        if (value !== prevValue) {
            setPrevValue(value);
            if (value !== undefined) {
                setInternalValue(value);
            }
        }

        const handleSelect = (selectedValue: string) => {
            const newValue = selectedValue === (value || internalValue) ? "" : selectedValue;
            if (onChange) {
                onChange(newValue);
            }
            setInternalValue(newValue);
            setOpen(false);
        };

        const comboTriggerClassConditions = {
            "text-content-primary": !!value,
            "shadow-faux-border-danger": !!error
        };

        return (
            <>
                {/* We add a hidden div to make sure the Select is focused on validation error (iOS-specific) */}
                <div tabIndex={-1} ref={ref} className="pointer-events-none absolute left-0 top-0 size-[1px] opacity-0"></div>
                <div ref={containerRef} className={wrapperClass}>
                    <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant={buttonVariant}
                                size={buttonSize}
                                role="combobox"
                                aria-expanded={open}
                                className={cn(
                                    "relative w-full min-w-[200px] justify-between border-none font-normal shadow-faux-border",
                                    comboTriggerClassConditions,
                                    variant === "floating-inner" && (value || internalValue) ? "pt-6" : ""
                                )}
                                disabled={disabled}
                                {...triggerProps}
                            >
                                <span className="absolute max-w-[80%] truncate sm:max-w-[85%]">
                                    {value || internalValue ? options.find(option => option.value === (value || internalValue))?.label : (placeholder ?? `Choose an option`)}
                                </span>
                                {isLoading ? (
                                    <div className="absolute right-3 z-10 flex h-11 items-center text-content-brand">
                                        <Loader2 className="animate-spin" />
                                    </div>
                                ) : (
                                    <ChevronsUpDown className={cn(iconVariants({ variant }), "absolute right-4 top-1/2 -translate-y-1/2")} />
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent
                            className={cn("z-[100] border border-stroke-primary bg-background-primary p-0", "width-equal-to-popover-trigger")}
                            style={{ pointerEvents: "auto" }}
                            onWheel={e => e.stopPropagation()}
                            onTouchMove={e => e.stopPropagation()}
                            container={container}
                        >
                            {lockedContent ? (
                                lockedContent(() => setOpen(false))
                            ) : (
                                <VirtualizedCommand
                                    height={height}
                                    options={options}
                                    keyByLabel={keyByLabel}
                                    searchPlaceholder={searchPlaceholder}
                                    selectedOption={internalValue}
                                    onSelectOption={currentValue => {
                                        handleSelect(currentValue);
                                    }}
                                    enableFuzzySearch={enableFuzzySearch}
                                />
                            )}
                        </PopoverContent>
                    </Popover>
                </div>
            </>
        );
    }
);

Combobox.displayName = "Combobox";
